<script setup>
import { computed } from 'vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import TabGroup from '../Atoms/TabGroup.vue'
import TabItem from '../Atoms/TabItem.vue'
import { LEVEL_LABELS } from '@/Utils/teamPermissionRows'

const props = defineProps({
	label: { type: String, required: true },
	description: { type: String, default: '' },
	// 'levels' → ordered ladder; 'switch' → binary on/off.
	kind: { type: String, default: 'levels' },
	// Ordered subset of ['none','view','edit','full'] this area offers.
	levels: { type: Array, default: () => ['none', 'view', 'edit', 'full'] },
	level: { type: String, default: 'none' },
	disabled: { type: Boolean, default: false },
	// Advanced flags: [{ flag, label, value }].
	advanced: { type: Array, default: () => [] },
	// Whether a content scope picker should render (slot) below when active.
	hasScope: { type: Boolean, default: false },
})

const emit = defineEmits(['update:level', 'toggle-advanced'])

const isOn = computed(() => props.level !== 'none')
// Advanced flags only make sense once the member can actually edit the stream,
// so they render inline at `edit`/`full` and are hidden at `none`/`view`.
const showAdvancedFlags = computed(() => props.level === 'edit' || props.level === 'full')
const cols = computed(() => props.levels.length)
const labelFor = (lvl) => LEVEL_LABELS[lvl] || lvl

const setLevel = (next) => {
	if (props.disabled || props.level === next) return
	emit('update:level', next)
}

const setSwitch = (on) => setLevel(on ? 'full' : 'none')

const onAdvanced = (flag, value) => emit('toggle-advanced', { flag, value: !!value })
</script>

<template>
	<div class="flex flex-col gap-2 py-3 border-b border-surface-3 last:border-b-0">
		<div class="flex flex-wrap items-center gap-x-3 gap-y-2 max-lg:flex-col max-lg:items-stretch">
			<div class="min-w-0 flex-1">
				<p class="mb-0 text-sm font-medium text-surface-text-2">{{ label }}</p>
				<p v-if="description" class="mb-0 mt-0.5 text-tiny text-surface-7">{{ description }}</p>
			</div>
			<div class="shrink-0 max-lg:w-full">
				<div v-if="kind === 'switch'" class="flex justify-end max-lg:justify-start">
					<FormSwitch :model-value="isOn" :disabled="disabled" @update:model-value="setSwitch" />
				</div>
				<TabGroup v-else type="segmented" size="sm" :cols="cols" class="max-lg:w-full">
					<TabItem
						v-for="lvl in levels"
						:key="lvl"
						:accent="lvl !== 'none'"
						:is-active="level === lvl"
						:disabled="disabled"
						@click="setLevel(lvl)"
					>
						{{ labelFor(lvl) }}
					</TabItem>
				</TabGroup>
			</div>
		</div>

		<!-- Level-specific modifier (e.g. "Moderate Chat only" at `view`). Filled by
		     the parent, which owns the level semantics; this row stays a renderer. -->
		<div v-if="$slots.modifier" class="mt-0.5">
			<slot name="modifier" />
		</div>

		<!-- Advanced flags that don't fit the ordered ladder (enabling the
		     stream itself, enabling its destinations). Rendered inline once the
		     member can edit the stream (edit/full); hidden at none/view. -->
		<div v-if="advanced.length && showAdvancedFlags" class="mt-0.5 flex flex-col gap-2">
			<div
				v-for="adv in advanced"
				:key="adv.flag"
				class="flex items-center justify-between gap-3"
			>
				<span class="text-tiny text-surface-8">{{ adv.label }}</span>
				<FormSwitch
					:model-value="!!adv.value"
					:disabled="disabled"
					@update:model-value="(v) => onAdvanced(adv.flag, v)"
				/>
			</div>
		</div>

		<!-- Scope picker (All / Specific), rendered by the parent via slot. -->
		<div v-if="hasScope && isOn">
			<slot name="scope" />
		</div>
	</div>
</template>
